import { useEffect, useCallback, useState, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from "react-redux";

import {
  Container,
  Card,
  CardContent,
  Typography,
  makeStyles,
  Box,
  Button,
  CircularProgress,
} from "@material-ui/core";

import Api from "lib/api/httpRequestHelper";

import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";

import componentStyles from "assets/theme/views";
import themeColorsDesingSystem from "assets/theme/ds-colors";

import GeneralData from "../dados-gerais/page";
import ShowcaseConfiguration from "../configuracao/page";
import IncludeProducts from "../incluir-produtos/page";
import TargetAudience from "../publico-alvo/page";
import WizardStepper from "./wizard-stepper";

import {
  setCurrentStep,
  setMaxCompletedStep,
  resetVitrineData,
  loadVitrineForEdit,
  setCachedPaymentOptions,
  setCachedPriceTables,
} from "redux/reducers/criarVitrineReducer";

import useConfirmDialog from "hooks/useConfirmDialog";

import {
  WIZARD_LEAVE_CONFIRM_MESSAGE,
  hasUnsavedWizardWork,
  isStepReady,
  getStepValidationHint,
} from "../utils/wizard-helpers";

import {
  mapBrandFromApi,
  mapPaymentFromDetail,
  mapPriceTablesFromDetail,
  mapVitrineDateFromApi,
  mapSelectedProductFromDetailItem,
  buildShowcasePayload,
} from "../utils/showcase-mappers";

import {
  mapPaymentOptionForSelect,
  mapPriceTableOption,
} from "../utils/showcase-config-helpers";

import { VITRINE_B2B_LIST_PATH } from "../utils/vitrine-b2b-routes";

const useStyles = makeStyles(componentStyles);

const steps = [
  { label: "Informações da vitrine", component: GeneralData },
  { label: "Configuração", component: ShowcaseConfiguration },
  { label: "Inclusão de produtos", component: IncludeProducts },
  { label: "Público alvo | Representantes", component: TargetAudience },
];

export default function VitrineWizardShell({ mode, vitrineId }) {
  const isEditMode = mode === "edit";
  const [loading, setLoading] = useState(false);
  const [loadingVitrine, setLoadingVitrine] = useState(isEditMode);
  const submitInProgressRef = useRef(false);
  const editLoadIdRef = useRef(null);

  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const snackbar = useSnackbar();
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const wizardState = useSelector(
    (state) => ({
      currentStep: state.criarVitrine.currentStep,
      maxCompletedStep: state.criarVitrine.maxCompletedStep,
      formData: state.criarVitrine.formData,
      selectedProducts: state.criarVitrine.selectedProducts,
      representatives: state.criarVitrine.representatives,
      editMode: state.criarVitrine.editMode,
      vitrineId: state.criarVitrine.vitrineId,
    }),
    shallowEqual,
  );

  const {
    currentStep,
    maxCompletedStep,
    formData,
    selectedProducts,
    representatives,
    editMode,
    vitrineId: vitrineIdFromStore,
  } = wizardState;

  const stepReady = useMemo(
    () => isStepReady(currentStep, wizardState),
    [currentStep, wizardState],
  );

  const stepHint = useMemo(
    () => getStepValidationHint(currentStep, wizardState),
    [currentStep, wizardState],
  );

  const handleLoadVitrineForEdit = useCallback(
    async (id) => {
      setLoadingVitrine(true);
      try {
        const response = await Api.get(
          "virtual-showcase-b2b-detail",
          "showcaseB2B",
          `?idVitrineVirtualB2b=${id}`,
          true,
        );

        const data = response?.Result;
        if (!data) {
          snackbar.showMessage("Vitrine não encontrada.", {
            severity: "error",
          });
          history.push(VITRINE_B2B_LIST_PATH);
          return;
        }

        let paymentOptions = [];
        let priceTableOptions = [];

        if (data.Marca) {
          try {
            const payRes = await Api.get(
              "list-b2b-payment-conditions-enabled",
              "showcaseB2B",
              `?idMarca=${data.Marca}`,
              true,
            );
            if (payRes?.Success && Array.isArray(payRes.Result)) {
              paymentOptions = payRes.Result.map(mapPaymentOptionForSelect);
            }
          } catch {
            paymentOptions = [];
          }
        }

        try {
          const tabRes = await Api.get(
            "list-b2b-showcase-price-tables",
            "showcaseB2B",
            "",
            true,
          );
          if (tabRes?.Success && Array.isArray(tabRes.Result)) {
            priceTableOptions = tabRes.Result.map(mapPriceTableOption).filter(
              (row) => row.CodTabelaPreco,
            );
          }
        } catch {
          priceTableOptions = [];
        }

        if (data.Marca) {
          dispatch(
            setCachedPaymentOptions({
              idMarca: data.Marca,
              options: paymentOptions,
            }),
          );
        }
        dispatch(setCachedPriceTables(priceTableOptions));

        const paymentConditions = mapPaymentFromDetail(
          data.CondicoesPagamento?.length > 0
            ? data.CondicoesPagamento
            : data.CondicaoPagamento
              ? [data.CondicaoPagamento]
              : [],
          paymentOptions,
        );

        const priceTablesSource =
          data.TabelasPreco?.length > 0
            ? data.TabelasPreco
            : data.CodigosTabelaPreco?.length > 0
              ? data.CodigosTabelaPreco
              : data.CodigoTabelaPreco != null
                ? [{ CodigoTabelaPreco: data.CodigoTabelaPreco }]
                : [];

        const priceTables = mapPriceTablesFromDetail(
          priceTablesSource,
          priceTableOptions,
        );

        const products = (data.Itens ?? []).map(mapSelectedProductFromDetailItem);

        const reps = (data.Representantes ?? []).map((r) => ({
          Id: r.IdPessoaRepresentante,
          Nome: r.NomeRepresentante,
          Cnpj: r.CnpjCpfRepresentante,
        }));

        dispatch(
          loadVitrineForEdit({
            vitrineId: data.IdVitrineVirtualB2b,
            status: data.Status,
            formData: {
              brand: mapBrandFromApi(data.Marca),
              name: data.Nome ?? "",
              description: data.Descricao ?? "",
              initialDate: data.FinalizarEstoque
                ? ""
                : mapVitrineDateFromApi(
                    data.DataVigenciaInicioFormat ||
                      data.dataVigenciaInicioFormat ||
                      data.DataVigenciaInicio ||
                      data.dataVigenciaInicio,
                  ),
              finalDate: data.FinalizarEstoque
                ? ""
                : mapVitrineDateFromApi(
                    data.DataVigenciaFimFormat ||
                      data.dataVigenciaFimFormat ||
                      data.DataVigenciaFim ||
                      data.dataVigenciaFim,
                  ),
              untilSoldOut: data.FinalizarEstoque ?? false,
              paymentConditions,
              paymentCondition: paymentConditions[0] ?? null,
              priceTables,
              priceTable: priceTables[0] ?? null,
              minimumValue: data.ValorMinimo ?? null,
              minimumInvoice: data.DuplicataMinima ?? 0,
              minimumQuantityPerOrder: data.QuantidadeMinimaPedido ?? "",
              byLine: data.PorLinha ?? false,
              byOrder: data.PorPedido ?? false,
            },
            selectedProducts: products,
            representatives: reps,
            cachedPaymentByMarca: data.Marca
              ? { [data.Marca]: paymentOptions }
              : undefined,
            cachedPriceTables: priceTableOptions,
          }),
        );
      } catch {
        snackbar.showMessage("Falha ao carregar vitrine para edição.", {
          severity: "error",
        });
        history.push(VITRINE_B2B_LIST_PATH);
      } finally {
        setLoadingVitrine(false);
      }
    },
    [dispatch, history, snackbar],
  );

  const handleSaveShowcase = useCallback(async () => {
    const payload = buildShowcasePayload(
      formData,
      selectedProducts,
      representatives,
    );

    if (isEditMode && vitrineIdFromStore) {
      const editResponse = await Api.put(
        "edit-virtual-showcase-b2b",
        "showcaseB2B",
        { idVitrineVirtualB2b: vitrineIdFromStore, ...payload },
        true,
      );

      if (!editResponse?.Success) {
        throw new Error(
          editResponse?.ValidationMessage || "Falha ao atualizar a vitrine.",
        );
      }

      return vitrineIdFromStore;
    }

    const response = await Api.post(
      "create-virtual-showcase-b2b",
      "showcaseB2B",
      payload,
      true,
    );

    if (!response?.Success) {
      throw new Error(
        response?.ValidationMessage || "Falha ao criar a vitrine.",
      );
    }

    return response.Result.IdVitrineVirtualB2b;
  }, [
    formData,
    selectedProducts,
    representatives,
    isEditMode,
    vitrineIdFromStore,
  ]);

  const tryLeaveWizard = useCallback(async () => {
    if (!hasUnsavedWizardWork(wizardState)) {
      return true;
    }
    return confirm(WIZARD_LEAVE_CONFIRM_MESSAGE, {
      title: "Descartar alterações",
      confirmLabel: "Sair",
      cancelLabel: "Continuar editando",
    });
  }, [wizardState, confirm]);

  const handleNextStep = useCallback(async () => {
    if (currentStep === 3) {
      if (submitInProgressRef.current) {
        return;
      }
      submitInProgressRef.current = true;
      setLoading(true);
      try {
        await handleSaveShowcase();
        snackbar.showMessage(
          isEditMode
            ? "Vitrine atualizada com sucesso!"
            : "Vitrine criada com sucesso!",
          { severity: "success" },
        );
        dispatch(resetVitrineData());
        history.push(VITRINE_B2B_LIST_PATH);
      } catch (error) {
        snackbar.showMessage(
          error?.message ||
            (isEditMode
              ? "Falha ao atualizar a vitrine. Tente novamente."
              : "Falha ao criar a vitrine. Tente novamente."),
          { severity: "error" },
        );
        console.error(error);
      } finally {
        setLoading(false);
        submitInProgressRef.current = false;
      }
    } else {
      dispatch(setMaxCompletedStep(currentStep + 1));
      dispatch(setCurrentStep(currentStep + 1));
    }
  }, [
    currentStep,
    handleSaveShowcase,
    dispatch,
    history,
    isEditMode,
    snackbar,
  ]);

  const handlePreviousStep = useCallback(async () => {
    if (currentStep === 0) {
      if (!(await tryLeaveWizard())) return;
      dispatch(resetVitrineData());
      history.push(VITRINE_B2B_LIST_PATH);
    } else {
      dispatch(setCurrentStep(currentStep - 1));
    }
  }, [currentStep, dispatch, history, tryLeaveWizard]);

  const handleCancel = useCallback(async () => {
    if (!(await tryLeaveWizard())) return;
    dispatch(resetVitrineData());
    history.push(VITRINE_B2B_LIST_PATH);
  }, [dispatch, history, tryLeaveWizard]);

  const handleStepClick = useCallback(
    (stepIndex) => {
      if (stepIndex === currentStep) return;
      if (stepIndex > maxCompletedStep) return;
      dispatch(setCurrentStep(stepIndex));
    },
    [currentStep, maxCompletedStep, dispatch],
  );

  useEffect(() => {
    if (!isEditMode) return;
    if (!vitrineId) {
      history.replace(VITRINE_B2B_LIST_PATH);
      return;
    }
    if (editLoadIdRef.current === vitrineId) return;
    editLoadIdRef.current = vitrineId;
    handleLoadVitrineForEdit(vitrineId);
  }, [isEditMode, vitrineId, handleLoadVitrineForEdit, history]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!hasUnsavedWizardWork(wizardState)) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [wizardState]);

  const pageTitle = isEditMode ? "Edição da Vitrine" : "Criação da Vitrine";
  const finalButtonLabel = isEditMode ? "Salvar Vitrine" : "Publicar Vitrine";
  const showWizard = !isEditMode || (editMode && !loadingVitrine);

  return (
    <>
      <ConfirmDialog />
      <HeaderClientes section="Pré-pedido" subsection="Vitrine B2B" />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <Card classes={{ root: classes.cardRoot }}>
          <CardContent>
            <Typography
              variant="h2"
              style={{
                color: themeColorsDesingSystem.primary.main,
                fontWeight: 700,
                marginBottom: 0,
              }}
            >
              {pageTitle}
            </Typography>

            <WizardStepper
              currentStep={currentStep}
              maxCompletedStep={maxCompletedStep}
              onStepClick={handleStepClick}
            />

            {loadingVitrine ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
              </Box>
            ) : showWizard ? (
              <Box mt={2}>
                {steps.map((step, index) => {
                  const StepComponent = step.component;
                  return (
                    <Box
                      key={step.label}
                      display={currentStep === index ? "block" : "none"}
                      aria-hidden={currentStep !== index}
                    >
                      <StepComponent />
                    </Box>
                  );
                })}
              </Box>
            ) : null}

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={5}
              pt={2}
              borderTop={`1px solid ${themeColorsDesingSystem.stone.main}`}
            >
              <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
                {!stepReady && stepHint && !loadingVitrine && (
                  <Typography
                    variant="caption"
                    style={{ color: themeColorsDesingSystem.gray.dark }}
                  >
                    {stepHint}
                  </Typography>
                )}
                <Box display="flex" style={{ gap: 12 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNextStep}
                    disabled={!stepReady || loading || loadingVitrine}
                    style={{
                      minWidth: 160,
                      display: "flex",
                      justifyContent: "center",
                    }}
                    startIcon={
                      loading && currentStep === 3 ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null
                    }
                  >
                    {loading && currentStep === 3
                      ? "Enviando..."
                      : currentStep === 3
                        ? finalButtonLabel
                        : "Avançar"}
                  </Button>
                  {currentStep > 0 && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handlePreviousStep}
                      disabled={loading || loadingVitrine}
                      style={{
                        minWidth: 120,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      Voltar
                    </Button>
                  )}
                  <Button
                    color="default"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </>
  );
}
